import { Fragment, useEffect, useState } from 'react';
import { FIGURE_MOUNTS, FIGURE_MOUNT_WORDS, FIGURE_PRINTER_TYPES, figurePrintCheck, plinthDesign } from '@boardflight/shared';
import type {
  AdminFigure,
  AdminFigureFile,
  AdminFigureSet,
  AdminFigures,
  AdminFiguresGame,
  AssemblyCheck,
  FigureFinish,
  FigureMount,
  FigureSettings,
  PlinthKey,
} from '@boardflight/shared';
import { api, blobUrl, download, post, put, upload } from '../api.js';
import { FilePreview, type PreviewFile, previewKindOf } from '../FilePreview.js';
import { Async, Panel, Time, useAsync, useSelection } from '../ui.js';

/**
 * Printed figures (docs/meshy-models-architecture.md §7): the operator's half.
 *
 * Top to bottom in the order an operator needs them: what Meshy has left and is doing, which of
 * Boardaux's games may make figures and on how many credits, the sets waiting for a print and a
 * decision, and every other set. A submitted set is assembled by the API's assembler onto its plinth,
 * once per bottom (docs/figure-plinths-plan.md §5.2); an operator looks at and test-prints what it
 * made, and then accepts the set or returns it with a note the team reads verbatim. A bottom the
 * assembler could not make is assembled again or sent back: there is no file of an operator's own to
 * stand in for it. Every file opens in the page (`FilePreview.tsx`), with Download inside. Prices and
 * listing come with the shop (F4).
 *
 * `#figures/<setId>` — the link in the submission email — scrolls to that set.
 */
export function Figures() {
  const state = useAsync(() => api<AdminFigures>('/v1/admin/figures'), []);
  const [selected] = useSelection('figures');

  useEffect(() => {
    if (!selected || !state.data) return;
    document.getElementById(`figure-set-${selected}`)?.scrollIntoView({ block: 'start' });
  }, [selected, state.data]);

  return (
    <Async state={state}>
      {(data) => {
        const waiting = data.sets.filter((set) => set.status === 'submitted');
        const others = data.sets.filter((set) => set.status !== 'submitted');
        return (
          <>
            <Panel title="Meshy">
              <Meshy data={data} />
            </Panel>

            <Panel title="Settings">
              <FigureSettingsForm />
            </Panel>

            <Panel title="Games">
              {data.games.length === 0 ? (
                <p className="muted">
                  No game can make figures. A game can once its team is marked as Boardaux’s own in <a href="#teams">Teams</a>{' '}
                  and it is developed in a playground.
                </p>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Game</th>
                        <th>Team</th>
                        <th>Figures</th>
                        <th className="num">Credits a month</th>
                        <th className="num">Used this month</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {data.games.map((game) => (
                        <GameRow key={game.id} game={game} onSaved={state.reload} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            <Panel title={`Waiting for review (${waiting.length})`}>
              {waiting.length === 0 ? (
                <p className="muted">Nothing is waiting. A set lands here when its team submits it.</p>
              ) : (
                waiting.map((set) => <Submitted key={set.id} set={set} focused={set.id === selected} onDecided={state.reload} />)
              )}
            </Panel>

            <Panel title="Other sets">
              {others.length === 0 ? (
                <p className="muted">None yet.</p>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Set</th>
                        <th>Game</th>
                        <th>Status</th>
                        <th className="num">Figures</th>
                        <th>Reviewed</th>
                        <th>Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {others.map((set) => (
                        <Fragment key={set.id}>
                        <tr id={`figure-set-${set.id}`}>
                          <td>{set.name}</td>
                          <td>
                            <a href={`#games/${set.app.id}`}>{set.app.name}</a> <span className="muted small">{set.app.teamName}</span>
                          </td>
                          <td>
                            <span className={set.status === 'accepted' || set.status === 'listed' ? 'pill on' : set.status === 'returned' ? 'pill off' : 'pill'}>
                              {set.status}
                            </span>
                          </td>
                          <td className="num">{set.figures.length}</td>
                          <td className="muted small">
                            {set.reviewedAt ? (
                              <>
                                <Time at={set.reviewedAt} /> {set.reviewedBy ? `by @${set.reviewedBy}` : ''}
                              </>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="prose small">{set.reviewNote ?? <span className="muted">—</span>}</td>
                        </tr>
                        {SELLABLE.has(set.status) ? (
                          <tr key={`${set.id}-sale`} className="sale-row">
                            <td colSpan={6}>
                              <ForSale set={set} onChanged={state.reload} />
                            </td>
                          </tr>
                        ) : null}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </>
        );
      }}
    </Async>
  );
}

function Meshy({ data }: { data: AdminFigures }) {
  const { balance, jobs } = data;
  return (
    <div className="row">
      {balance ? (
        <span>
          <strong className="num">{balance.balance}</strong> credits left ({balance.monthlyCredits} this month,{' '}
          {balance.permanentCredits} bought), read <Time at={balance.readAt} />. The month resets <Time at={balance.monthlyResetsAt} />.
        </span>
      ) : (
        <span className="muted">No balance read yet. The API reads it hourly once its Meshy key is set.</span>
      )}
      <span className="muted small">
        {jobs.running} running · {jobs.waiting} waiting
        {jobs.failedThisWeek > 0 ? <span className="warn"> · {jobs.failedThisWeek} failed this week</span> : null}
      </span>
    </div>
  );
}

function GameRow({ game, onSaved }: { game: AdminFiguresGame; onSaved: () => void }) {
  const [credits, setCredits] = useState(String(game.creditsMonthly));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(on: boolean) {
    const value = Number(credits);
    if (!Number.isInteger(value) || value < 0) {
      setError('Credits are a whole number.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/admin/apps/${game.id}/figures-shop`, {
        method: 'PATCH',
        body: JSON.stringify({ on, creditsMonthly: value }),
      });
      onSaved();
    } catch (failure) {
      setError(String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr>
      <td>
        <a href={`#games/${game.id}`}>{game.name}</a>
      </td>
      <td>{game.teamName}</td>
      <td>
        <span className={game.shopOn ? 'pill on' : 'pill'}>{game.shopOn ? 'on' : 'off'}</span>
      </td>
      <td className="num">
        <input
          className="credits-input"
          type="number"
          min={0}
          step={10}
          value={credits}
          onChange={(event) => setCredits(event.target.value)}
          aria-label={`Monthly credits for ${game.name}`}
        />
      </td>
      <td className="num">{game.creditsUsed}</td>
      <td className="nowrap-row">
        <button disabled={busy} onClick={() => void save(game.shopOn)}>
          Save credits
        </button>
        <button className={game.shopOn ? 'danger' : 'primary'} disabled={busy} onClick={() => void save(!game.shopOn)}>
          {game.shopOn ? 'Turn off' : 'Turn on'}
        </button>
        {error ? <span className="error small">{error}</span> : null}
      </td>
    </tr>
  );
}

/** A figure's own files in a review, in the order they are looked at. Each opens in the page. */
const FILE_LABELS: [AdminFigureFile, string][] = [
  ['print.3mf', '3MF'],
  ['print.stl', 'STL'],
  ['painted.glb', 'Painted model'],
  ['view.glb', 'Unpainted model'],
  ['front.png', 'Front'],
  ['right.png', 'Right'],
  ['back.png', 'Back'],
  ['left.png', 'Left'],
];

/**
 * Where the dashboard serves the plinth library's pictures, `/plinths/<key>.webp` (plinths plan §2.2).
 * Compiled in, like the dashboard's URL in `App.tsx`; a picture that does not load leaves the name.
 */
const PLINTH_PICTURES = `${(import.meta.env.VITE_WEB_BASE_URL ?? 'https://boardflight.app').replace(/\/+$/, '')}/plinths`;

/** Which bottoms of each figure the assembler has not made, so have nothing to print. */
function missingBottoms(set: AdminFigureSet): string[] {
  return set.figures.flatMap((figure) => {
    const missing = FIGURE_MOUNTS.filter((mount) => !figure.assemblies[mount]);
    return missing.length ? [`${figure.name} (Base ${figure.glyph}): ${missing.map((mount) => FIGURE_MOUNT_WORDS[mount].name.toLowerCase()).join(' and ')}`] : [];
  });
}

function Submitted({ set, focused, onDecided }: { set: AdminFigureSet; focused: boolean; onDecided: () => void }) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const missing = missingBottoms(set);

  async function act(work: () => Promise<unknown>, after?: () => void) {
    setBusy(true);
    setError(null);
    try {
      await work();
      after?.();
      onDecided();
    } catch (failure) {
      setError(String(failure));
    } finally {
      setBusy(false);
    }
  }

  const decide = (decision: 'accept' | 'return') =>
    void act(
      () => post(`/v1/admin/figures/sets/${set.id}/${decision}`, decision === 'return' ? { note: note.trim() } : {}),
      () => setNote(''),
    );

  return (
    <article id={`figure-set-${set.id}`} className={focused ? 'review focused' : 'review'}>
      <header className="review-head">
        <Plinth plinth={set.plinth} />
        <div>
          <h3>{set.name}</h3>
          <div className="muted small">
            {set.app.name} · {set.app.teamName} · submitted <Time at={set.updatedAt} /> · {set.figures.length} figures
          </div>
        </div>
      </header>

      <Assembly set={set} busy={busy} onAssemble={() => void act(() => post(`/v1/admin/figures/sets/${set.id}/assemble`, {}))} />

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th className="num">Base</th>
              <th>Figure</th>
              <th className="num">Height</th>
              <th title="Advisory. The assembler's checks under each bottom are what decide.">Meshy’s print check</th>
              <th>Model</th>
              {FIGURE_MOUNTS.map((mount) => (
                <th key={mount}>{FIGURE_MOUNT_WORDS[mount].name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {set.figures.map((figure) => (
              <FigureRow key={figure.id} figure={figure} />
            ))}
          </tbody>
        </table>
      </div>

      <textarea
        className="review-note"
        rows={2}
        placeholder="Note to the team, required to return the set. They read it verbatim."
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />
      <div className="row">
        <button
          className="primary"
          disabled={busy || !set.assembled}
          title={!set.assembled ? 'Every figure needs to be assembled on both bottoms first.' : undefined}
          onClick={() => decide('accept')}
        >
          {busy ? 'Saving…' : 'Accept the set'}
        </button>
        <button
          className="danger"
          disabled={busy || note.trim() === ''}
          title={note.trim() === '' ? 'Say what needs to change first.' : undefined}
          onClick={() => decide('return')}
        >
          Return to the team
        </button>
        <span className="muted small">
          {missing.length > 0 ? `Not assembled yet: ${missing.join('; ')}.` : 'Accepting is not listing: prices and the shelf come next.'}
        </span>
      </div>
      {error ? <p className="error">{error}</p> : null}
    </article>
  );
}

/** A set's plinth as its round picture and name, or the name alone where the picture cannot load. */
function Plinth({ plinth }: { plinth: PlinthKey | null }) {
  const [broken, setBroken] = useState(false);
  if (!plinth) return <span className="plinth-pick error small">No plinth</span>;
  const name = plinthDesign(plinth)?.name ?? plinth;
  return (
    <span className="plinth-pick" title={`Plinth: ${name}`}>
      {broken ? null : <img src={`${PLINTH_PICTURES}/${plinth}.webp`} alt="" onError={() => setBroken(true)} />}
      <span className="small">{name}</span>
    </span>
  );
}

/**
 * The set's latest assembly job and *Assemble again*. The console only queues the job; the API's
 * coordinator starts the sandbox, so a queued job waits for its next pass.
 */
function Assembly({ set, busy, onAssemble }: { set: AdminFigureSet; busy: boolean; onAssemble: () => void }) {
  const job = set.assembly;
  const working = job?.status === 'queued' || job?.status === 'running';
  const pill = !job ? (
    <span className="pill">Not assembled</span>
  ) : job.status === 'queued' ? (
    <span className="pill">Queued</span>
  ) : job.status === 'running' ? (
    <span className="pill paid">Assembling</span>
  ) : job.status === 'done' ? (
    <span className="pill on">Ready</span>
  ) : (
    <span className="pill off">Failed</span>
  );
  const urge = !working && (!job || job.status === 'failed' || job.stale || !set.assembled);
  return (
    <div className="row assembly-state">
      <span className="nowrap-row">
        <span className="muted small">Assembly</span> {pill}
        {job?.stale ? (
          <span className="pill" title="Made with another plinth, library version or bottom setting than today’s.">
            Stale
          </span>
        ) : null}
      </span>
      {job ? (
        <span className="muted small">
          {working ? 'since' : 'finished'} <Time at={job.updatedAt} />
          {job.attempts > 1 ? ` · attempt ${job.attempts}` : ''}
        </span>
      ) : null}
      <button
        className={urge ? 'primary' : undefined}
        disabled={busy || working || !set.plinth}
        title={!set.plinth ? 'The set has no plinth.' : working ? 'Wait for this one to finish.' : undefined}
        onClick={onAssemble}
      >
        Assemble again
      </button>
      {job?.error ? <span className="error small">{job.error}</span> : null}
      {job?.stale && !working ? <span className="warn small">A bottom setting or the plinth changed since. Assemble again to print today’s.</span> : null}
    </div>
  );
}

const PRINTER_WORDS: Record<string, string> = { fdm: 'filament printer', sla: 'resin printer', full_color: 'full-colour printer' };

/** A share of a surface as a person says it: 14%, or under 1% rather than a misleading 0%. */
function percent(share: number): string {
  const whole = Math.round(share * 100);
  return whole === 0 && share > 0 ? 'under 1%' : `${whole}%`;
}

/**
 * What Meshy's print check found, one finding a line in plain words. Meshy's own verdict is a single
 * word, and on the first real set it said `error` for a surface that was closed and sound but partly
 * thinner than a filament printer's minimum wall, which read as a failure it was not. The check is
 * advisory: the assembler's checks under each bottom are what gate a set. So nothing here is drawn
 * as an error; a finding is a warning at most, and Meshy's word stays in the tooltip.
 */
function printCheckLines(raw: unknown): { lines: string[]; warn: boolean; status: string | null } {
  const check = figurePrintCheck(raw);
  if (!check) return { lines: [], warn: false, status: null };
  const lines: string[] = [];
  if (check.holes > 0) lines.push(`Holes: ${check.holes}`);
  if (check.brokenEdges > 0) lines.push(`Broken edges: ${check.brokenEdges}`);
  if (check.sealed === false && check.holes === 0 && check.brokenEdges === 0) lines.push('Surface not closed');
  if (check.thinWallsFail && check.thinShare !== null) {
    const under = check.wallMm !== null ? `under ${check.wallMm} mm` : 'too thin';
    const printer = check.printer ? PRINTER_WORDS[check.printer] ?? check.printer : null;
    lines.push(`Thin walls: ${percent(check.thinShare)} ${under}${printer ? ` (${printer})` : ''}`);
  } else if (check.thinWallsFail) {
    lines.push('Thin walls');
  }
  if (lines.length > 0) return { lines, warn: true, status: check.status };
  return { lines: [check.sealed === true ? 'Closed, no issues' : 'No issues found'], warn: false, status: check.status };
}

function PrintCheck({ figure }: { figure: AdminFigure }) {
  const { lines, warn, status } = printCheckLines(figure.printability);
  return (
    <div className={warn ? 'print-check warn' : 'print-check'} title={status ? `Meshy’s verdict: ${status}. Advisory only.` : undefined}>
      {lines.length === 0 ? <span className="muted">Not checked</span> : lines.map((line) => <span key={line}>{line}</span>)}
      {figure.repaired ? <span className="muted">Repaired by Meshy</span> : null}
    </div>
  );
}

/** The model formats a file chip opens, and which of them draw in plain grey. */
function previewOf(figure: AdminFigure, file: AdminFigureFile, label: string): PreviewFile {
  return {
    path: `/v1/admin/figures/${figure.id}/files/${file}`,
    label: `${figure.name} (Base ${figure.glyph}), ${label}`,
    kind: previewKindOf(file) ?? 'image',
    plain: file !== 'painted.glb',
  };
}

function FigureRow({ figure }: { figure: AdminFigure }) {
  const [open, setOpen] = useState<PreviewFile | null>(null);
  const files = FILE_LABELS.filter(([file]) => figure.files.includes(file));

  return (
    <tr>
      <td className="num">{figure.glyph}</td>
      <td>
        {figure.name} <span className="muted small">{figure.role}</span>
        {figure.take > 1 ? <span className="muted small"> · take {figure.take}</span> : null}
      </td>
      <td className="num nowrap">
        {figure.heightMm ?? '—'} mm {figure.form === 'flat' ? 'thick' : 'tall'}
        {figure.heightMm !== null && figure.heightMm !== figure.requestedHeightMm ? (
          <span className="muted small" title="Scaled down so it is no wider than the Base.">
            {' '}
            (asked {figure.requestedHeightMm})
          </span>
        ) : null}
      </td>
      <td className="small">
        <PrintCheck figure={figure} />
      </td>
      <td>
        <div className="figure-files">
          {files.map(([file, label]) => (
            <button key={file} className="chip" onClick={() => setOpen(previewOf(figure, file, label))}>
              {label}
            </button>
          ))}
        </div>
        {/* In a cell, since a row holds only cells; the dialog draws in the top layer wherever it is. */}
        {open ? <FilePreview file={open} onClose={() => setOpen(null)} /> : null}
      </td>
      {FIGURE_MOUNTS.map((mount) => (
        <td key={mount}>
          <Bottom figure={figure} mount={mount} onOpen={setOpen} />
        </td>
      ))}
    </tr>
  );
}

/** The assembler's checks in words an operator reads; a check it adds later shows by its own name. */
const CHECK_WORDS: Record<string, string> = {
  plinth_solid: 'Plinth is one solid',
  footprint_inside: 'Footprint on the plinth',
  clear_of_number: 'Figure clear of the number',
  knob_diameter: 'Knob diameter',
  knob_height: 'Knob height',
  flat_bottom: 'Flat bottom',
  height: 'Height',
  engraved: 'Number engraved',
};

function measured(value: unknown): string {
  if (typeof value === 'number') return String(Number(value.toFixed(3)));
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '—';
  return JSON.stringify(value);
}

function Check({ check }: { check: AssemblyCheck }) {
  return (
    <li className={check.ok ? '' : 'error'} title={`Expected ${check.expected}`}>
      <span className={check.ok ? 'ok' : 'error'}>{check.ok ? 'pass' : 'fail'}</span> {CHECK_WORDS[check.name] ?? check.name.replace(/_/g, ' ')}:{' '}
      {measured(check.measured)}
      {check.ok ? null : <span className="muted"> (expected {check.expected})</span>}
    </li>
  );
}

/**
 * A bottom's checks: a failed one always shows, with what it measured and expected; the ones that
 * passed fold under one line, since a passing set is most of what an operator reviews.
 */
function Checks({ checks }: { checks: AssemblyCheck[] }) {
  const failed = checks.filter((check) => !check.ok);
  const passed = checks.filter((check) => check.ok);
  return (
    <div className="small">
      {failed.length > 0 ? (
        <ul className="assembly-checks">
          {failed.map((check) => (
            <Check key={check.name} check={check} />
          ))}
        </ul>
      ) : null}
      {passed.length > 0 ? (
        <details className="assembly-passed">
          <summary className={failed.length ? 'muted' : 'ok'}>
            {failed.length ? `${passed.length} other checks pass` : `All ${passed.length} checks pass`}
          </summary>
          <ul className="assembly-checks">
            {passed.map((check) => (
              <Check key={check.name} check={check} />
            ))}
          </ul>
        </details>
      ) : failed.length === 0 ? (
        <span className="muted">No checks reported</span>
      ) : null}
    </div>
  );
}

/**
 * One figure on one bottom: what the assembler made of it, its checks, and its files, which open in
 * the page. How far the pose reaches past the rim is information, not a fault: a pose may overhang
 * the plinth and the Base, and the reviewer decides, returning the set if it is too much (the owner's
 * answer to plinths plan §10, item 7).
 */
function Bottom({ figure, mount, onOpen }: { figure: AdminFigure; mount: FigureMount; onOpen: (file: PreviewFile) => void }) {
  const assembly = figure.assemblies[mount];
  if (!assembly) return <span className="muted small">Not assembled</span>;
  const words = FIGURE_MOUNT_WORDS[mount].name.toLowerCase();
  return (
    <div className="bottom-cell">
      <Checks checks={assembly.checks} />
      <div className="muted small">
        {assembly.overhangMm === null ? null : assembly.overhangMm > 0 ? `Overhangs the rim by ${measured(assembly.overhangMm)} mm` : 'Inside the rim'}
      </div>
      <div className="figure-files">
        <button className="chip" onClick={() => onOpen(previewOf(figure, `assembled.${mount}.3mf`, `${words} 3MF`))}>
          3MF
        </button>
        <button className="chip" onClick={() => onOpen(previewOf(figure, `assembled.${mount}.stl`, `${words} STL`))}>
          STL
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- selling (F4) */

/** Sets an operator may price, photograph and put on the shelf: their print was accepted. */
const SELLABLE = new Set(['accepted', 'listed', 'unlisted']);

const FINISH_LABELS: Record<FigureFinish, string> = { unpainted: 'Unpainted', painted: 'Hand-painted' };

function dollars(cents: number | undefined): string {
  return cents === undefined ? '' : (cents / 100).toFixed(2);
}

/**
 * A set's shop panel: its price in each finish, a photo of each, and the shelf. The API refuses to
 * list a set without a price, a photo of every priced finish and every figure assembled on both
 * bottoms, and this says which is missing before anybody presses List.
 */
function ForSale({ set, onChanged }: { set: AdminFigureSet; onChanged: () => void }) {
  const priceOf = (finish: FigureFinish) => set.prices.find((price) => price.finish === finish)?.priceCents;
  const [prices, setPrices] = useState<Record<FigureFinish, string>>({
    unpainted: dollars(priceOf('unpainted')),
    painted: dollars(priceOf('painted')),
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function act(work: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await work();
      onChanged();
    } catch (failure) {
      setError(String(failure));
    } finally {
      setBusy(false);
    }
  }

  function savePrices() {
    const list = (['unpainted', 'painted'] as const).flatMap((finish) => {
      const text = prices[finish].trim();
      if (text === '') return [];
      const cents = Math.round(Number(text) * 100);
      return Number.isFinite(cents) && cents > 0 ? [{ finish, priceCents: cents }] : [];
    });
    void act(() => put(`/v1/admin/figures/sets/${set.id}/prices`, { prices: list }));
  }

  const priced = set.prices.map((price) => price.finish);
  const missing = [
    priced.length === 0 ? 'a price' : null,
    ...priced.filter((finish) => !set.photos[finish]).map((finish) => `a ${FINISH_LABELS[finish].toLowerCase()} photo`),
    set.assembled ? null : 'every figure assembled on both bottoms',
  ].filter((item): item is string => item !== null);

  return (
    <div className="for-sale">
      <div className="for-sale-prices">
        {(['unpainted', 'painted'] as const).map((finish) => (
          <label key={finish} className="for-sale-price">
            <span className="small muted">{FINISH_LABELS[finish]}, USD</span>
            <input
              className="credits-input"
              inputMode="decimal"
              placeholder="not sold"
              value={prices[finish]}
              onChange={(event) => setPrices((was) => ({ ...was, [finish]: event.target.value }))}
              aria-label={`${FINISH_LABELS[finish]} price in dollars for ${set.name}`}
            />
          </label>
        ))}
        <button disabled={busy} onClick={savePrices}>
          Save prices
        </button>
      </div>
      <div className="for-sale-photos">
        {(['unpainted', 'painted'] as const).map((finish) => (
          <SetPhoto key={finish} set={set} finish={finish} busy={busy} onUpload={(file) => act(() => upload(`/v1/admin/figures/sets/${set.id}/photo?finish=${finish}`, file, file.type || 'application/octet-stream'))} />
        ))}
      </div>
      <div className="row">
        {set.status === 'listed' ? (
          <button className="danger" disabled={busy} onClick={() => void act(() => post(`/v1/admin/figures/sets/${set.id}/unlist`, {}))}>
            Take off the shelf
          </button>
        ) : (
          <button className="primary" disabled={busy || missing.length > 0} onClick={() => void act(() => post(`/v1/admin/figures/sets/${set.id}/list`, {}))}>
            Put on the shelf
          </button>
        )}
        <span className="muted small">
          {set.status === 'listed'
            ? `On the shelf since ${set.listedAt ? new Date(set.listedAt).toLocaleDateString() : 'today'}. Orders keep their sets whatever happens here.`
            : missing.length > 0
              ? `Needs ${missing.join(', ')}.`
              : 'Ready for the shelf. Players see it on the game’s library page while the shop is open.'}
        </span>
      </div>
      {error ? <p className="error small">{error}</p> : null}
    </div>
  );
}

function SetPhoto({
  set,
  finish,
  busy,
  onUpload,
}: {
  set: AdminFigureSet;
  finish: FigureFinish;
  busy: boolean;
  onUpload: (file: File) => void;
}) {
  const path = set.photos[finish];
  const [url, setUrl] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!path) return;
    let made: string | null = null;
    let live = true;
    blobUrl(path).then(
      (next) => {
        made = next;
        if (live) setUrl(next);
        else URL.revokeObjectURL(next);
      },
      () => undefined,
    );
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [path, set.listedAt, set.updatedAt]);

  return (
    <div className="set-photo">
      {path && url ? (
        <button className="photo-open" title="Open" onClick={() => setOpen(true)}>
          <img src={url} alt={`${set.name}, ${FINISH_LABELS[finish].toLowerCase()}`} />
        </button>
      ) : (
        <div className="set-photo-empty muted small">No photo</div>
      )}
      {open && path ? (
        <FilePreview file={{ path, label: `${set.name}, ${FINISH_LABELS[finish].toLowerCase()} photo`, kind: 'image' }} onClose={() => setOpen(false)} />
      ) : null}
      <label className="btn file-pick">
        {path ? `Replace ${FINISH_LABELS[finish].toLowerCase()}` : `${FINISH_LABELS[finish]} photo`}
        <input type="file" accept="image/jpeg,image/png,image/webp" disabled={busy} onChange={(event) => event.target.files?.[0] && onUpload(event.target.files[0])} />
      </label>
    </div>
  );
}

/* ----------------------------------------------------------------------------- settings */

const PRINTER_LABELS: Record<string, string> = { sla: 'Resin (SLA)', fdm: 'Filament (FDM)', full_color: 'Full colour' };

/**
 * The deployment's figures and shop settings (migration 0097): what were vars, changed here and in
 * effect on the API's next request. The keys are not here: Meshy's and Stripe's stay deployment
 * secrets. Saved together with one button, so a half-edited form never opens the shop.
 */
function FigureSettingsForm() {
  const state = useAsync(() => api<FigureSettings>('/v1/admin/figure-settings'), []);
  return <Async state={state}>{(settings) => <SettingsFields key={settings.updatedAt ?? 0} settings={settings} onSaved={state.reload} />}</Async>;
}

function SettingsFields({ settings, onSaved }: { settings: FigureSettings; onSaved: () => void }) {
  const [shopOpen, setShopOpen] = useState(settings.shopOpen);
  const [leadDays, setLeadDays] = useState(String(settings.leadDays));
  const [rates, setRates] = useState(settings.shippingRates.join(', '));
  const [tax, setTax] = useState(settings.tax);
  const [lowBalance, setLowBalance] = useState(String(settings.meshyLowBalance));
  const [concurrency, setConcurrency] = useState(String(settings.meshyConcurrency));
  const [printer, setPrinter] = useState(settings.printerType);
  const [minMm, setMinMm] = useState(settings.heightMinMm === null ? '' : String(settings.heightMinMm));
  const [maxMm, setMaxMm] = useState(settings.heightMaxMm === null ? '' : String(settings.heightMaxMm));
  const [knobDiameter, setKnobDiameter] = useState(String(settings.knobDiameterMm));
  const [knobHeight, setKnobHeight] = useState(String(settings.knobHeightMm));
  const [engraveDepth, setEngraveDepth] = useState(String(settings.engraveDepthMm));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (shopOpen && !settings.shopOpen && !window.confirm('Open the shop? Every listed set goes on sale on its game’s library page.')) return;
    setBusy(true);
    setError(null);
    try {
      await put('/v1/admin/figure-settings', {
        shopOpen,
        leadDays: Number(leadDays),
        shippingRates: rates
          .split(',')
          .map((rate) => rate.trim())
          .filter(Boolean),
        tax,
        meshyLowBalance: Number(lowBalance),
        meshyConcurrency: Number(concurrency),
        printerType: printer,
        heightMinMm: minMm.trim() === '' ? null : Number(minMm),
        heightMaxMm: maxMm.trim() === '' ? null : Number(maxMm),
        knobDiameterMm: Number(knobDiameter),
        knobHeightMm: Number(knobHeight),
        engraveDepthMm: Number(engraveDepth),
      });
      onSaved();
    } catch (failure) {
      setError(String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="figure-settings">
      <fieldset>
        <legend>Shop</legend>
        <label className="filter-inline">
          <input type="checkbox" checked={shopOpen} onChange={(e) => setShopOpen(e.target.checked)} /> Open: listed sets are on sale
        </label>
        <label>
          <span className="small muted">Ships within, days</span>
          <input className="credits-input" type="number" min={1} max={120} value={leadDays} onChange={(e) => setLeadDays(e.target.value)} />
        </label>
        <label>
          <span className="small muted">Stripe shipping rate ids, comma-separated</span>
          <input value={rates} placeholder="shr_…" onChange={(e) => setRates(e.target.value)} />
        </label>
        <label className="filter-inline">
          <input type="checkbox" checked={tax} onChange={(e) => setTax(e.target.checked)} /> Stripe Tax computes US sales tax
        </label>
        <span className="muted small">US addresses only. The Stripe keys are deployment secrets, set with wrangler.</span>
      </fieldset>
      <fieldset>
        <legend>Making figures</legend>
        <label>
          <span className="small muted">Meshy tasks at once</span>
          <input className="credits-input" type="number" min={1} max={10} value={concurrency} onChange={(e) => setConcurrency(e.target.value)} />
        </label>
        <label>
          <span className="small muted">Printer the print check assumes</span>
          <select value={printer} onChange={(e) => setPrinter(e.target.value as FigureSettings['printerType'])}>
            {FIGURE_PRINTER_TYPES.map((type) => (
              <option key={type} value={type}>
                {PRINTER_LABELS[type] ?? type}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="small muted">Warn operators below, credits</span>
          <input className="credits-input" type="number" min={0} value={lowBalance} onChange={(e) => setLowBalance(e.target.value)} />
        </label>
        <label>
          <span className="small muted">Limit standing heights, mm (optional)</span>
          <span className="nowrap-row">
            <input className="credits-input" type="number" min={1} max={200} placeholder="min" value={minMm} onChange={(e) => setMinMm(e.target.value)} />
            <span className="muted">to</span>
            <input className="credits-input" type="number" min={1} max={200} placeholder="max" value={maxMm} onChange={(e) => setMaxMm(e.target.value)} />
          </span>
        </label>
        <span className="muted small">
          The agent picks each piece’s size: standing 8 to 60 mm tall, flat 2 to 12 mm thick, never wider than the 25 mm Base. Limits here only narrow
          the standing range.
        </span>
      </fieldset>
      <fieldset>
        <legend>Plinth bottoms</legend>
        <label>
          <span className="small muted">Snap-on knob, mm across</span>
          <input className="credits-input" type="number" min={6} max={14} step={0.05} value={knobDiameter} onChange={(e) => setKnobDiameter(e.target.value)} />
        </label>
        <label>
          <span className="small muted">Knob height, mm</span>
          <input className="credits-input" type="number" min={0.4} max={3} step={0.05} value={knobHeight} onChange={(e) => setKnobHeight(e.target.value)} />
        </label>
        <label>
          <span className="small muted">Base number cut, mm deep</span>
          <input className="credits-input" type="number" min={0.2} max={1} step={0.05} value={engraveDepth} onChange={(e) => setEngraveDepth(e.target.value)} />
        </label>
        <SnapPiece settings={settings} busy={busy} onUploaded={onSaved} />
        <span className="muted small">
          The knob clicks into the TPU snap piece’s recess, 10 mm wide and 1.4 mm deep. A change applies to the next assembly: sets assembled before it
          show as stale in review, with Assemble again.
        </span>
      </fieldset>
      <div className="row">
        <button className="primary" disabled={busy} onClick={() => void save()}>
          {busy ? 'Saving…' : 'Save settings'}
        </button>
        <span className="muted small">
          {settings.updatedAt ? (
            <>
              Last changed <Time at={settings.updatedAt} />. Takes effect at once.
            </>
          ) : (
            'Takes effect at once.'
          )}
        </span>
      </div>
      {error ? <p className="error small">{error}</p> : null}
    </div>
  );
}

/**
 * The TPU snap piece's file (plinths plan §0.5): uploaded once from the owner's design, kept as it
 * arrived, and downloaded whenever more pieces are printed. Its own upload rather than part of Save,
 * since it is a file and takes effect the moment it lands.
 */
function SnapPiece({ settings, busy, onUploaded }: { settings: FigureSettings; busy: boolean; onUploaded: () => void }) {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send(file: File | undefined) {
    if (!file) return;
    setSending(true);
    setError(null);
    try {
      await upload('/v1/admin/figure-settings/snap-piece', file, 'model/3mf');
      onUploaded();
    } catch (failure) {
      setError(String(failure));
    } finally {
      setSending(false);
    }
  }

  // A div, not a label: a label around the download button would press it for every click inside.
  return (
    <div className="settings-file">
      <span className="small muted">TPU snap piece</span>
      <span className="nowrap-row">
        {settings.snapPiece ? (
          <button className="chip on" onClick={() => void download('/v1/admin/figure-settings/snap-piece').catch((failure) => setError(String(failure)))}>
            3MF
          </button>
        ) : (
          <span className="muted small">No file yet</span>
        )}
        <label className="btn file-pick">
          {sending ? 'Uploading…' : settings.snapPiece ? 'Replace' : 'Upload 3MF'}
          <input type="file" accept=".3mf,model/3mf" disabled={busy || sending} onChange={(event) => void send(event.target.files?.[0])} />
        </label>
      </span>
      {error ? <span className="error small">{error}</span> : null}
    </div>
  );
}
